import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import bbox from "@turf/bbox";
import { Alert, TreeSelect } from "antd";
import classnames from "classnames";
import { SHAPEFILE_FIELDS } from "constants/settings.constants";
import { setCategoryColumns } from "store/features";
import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";
import styled from "styled-components";
import { parseShapefileProperty } from "utils/column/parseShapefileProperty";

import {
  GetOrgLevelShapefilesInProject,
  GetShapefileBranch,
  GetShapefileFeatures
} from "api/project";

import {
  IGroupByListColumn,
  buildShapefileProperty,
  isShapefileBoundaryProperty
} from "models";
import { PropertyFilterModel } from "models/filter";
import { ShapefileFileType } from "models/projects";

import { useProjectShapefilesQuery } from "components/project/shapefiles/queries";

import { EntityKind } from "../../models/entityKind";
import { useGroupByStore } from "./GroupByStoreProvider";
import ShapefileFieldOptions, {
  SHAPEFILE_FIELD_OPTIONS_TOOLBAR_COUNT
} from "./ShapefileFieldOptions";
import {
  selectEntityKind,
  selectProps,
  selectSelectedShapefile
} from "./groupBySelectors";
import { useSelectedCategory } from "./hooks";

export const ShapefileCategoryName = SHAPEFILE_FIELDS;

/**
 * The category's own toolbar slots: the tree select, then the options block,
 * which owns however many slots it reports.
 */
const TREE_SELECT_TOOLBAR_INDEX = 0;
const FIELD_OPTIONS_TOOLBAR_INDEX = TREE_SELECT_TOOLBAR_INDEX + 1;

/**
 * The shapefile this project is filtering on, read out of the applied filter:
 * the boundary predicate carries the shapefile's id in its property key, so
 * the picker can be put back where it was without the selection being kept
 * anywhere. Only this project's own predicate counts — a filter can hold one
 * per shapefile, and a shapefile from another project is not in this tree.
 */
function filteringShapefileId(
  propertiesFilter: Maybe<PropertyFilterModel[]>,
  projectId: Maybe<string>
): Maybe<string> {
  if (!projectId) {
    return null;
  }
  for (const propertyFilter of propertiesFilter ?? []) {
    for (const predicate of propertyFilter?.predicates ?? []) {
      if (!isShapefileBoundaryProperty(predicate?.property)) {
        continue;
      }
      const parsed = parseShapefileProperty(predicate.property);
      if (parsed?.projectId === projectId) {
        return parsed.shapefileId;
      }
    }
  }
  return null;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
interface ShapefileCategoryProps {
  toolbarActive?: boolean;
  toolbarFocusedIndex?: Maybe<number>;
  onSelectRef?: (index: number, selectEl: Maybe<any>) => void;
}

function ShapefileCategory({
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef
}: ShapefileCategoryProps = {}) {
  const dispatch = useDispatch();
  const entityKind = useGroupByStore(selectEntityKind);
  const { isShapefileCategory } = useSelectedCategory();
  const projectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  const propertiesFilter = useSelector(
    (state: RootState) => state.filter.propertiesFilter
  );
  const [shapefiles, setShapefiles] = useState([]);
  const shapefilesDictRef = useRef<{ [shapefile: string]: any }>({});
  const [selectedShapefile, setSelectedShapefile] = useState<string>();
  const [isGeoTiffSelected, setIsGeoTiffSelected] = useState(false);
  // What was picked last time this category was on screen. It is held by the
  // picker, which outlives the category, so switching to another field tab and
  // back finds the shapefile still selected.
  const rememberedShapefile = useGroupByStore(selectSelectedShapefile);
  const rememberShapefile = useGroupByStore((s) => s.setSelectedShapefile);
  const [errorMessage, setErrorMessage] = useState<string>();
  const setFlyToExtent = useMapStore((s) => s.setFlyToExtent);
  const storeProps = useGroupByStore(selectProps);
  // Only the flyout that owns a focus drives the main surface. Every other
  // GroupBy consumer (ML pickers, normalize-by, XDA, chart axes) leaves this
  // false, and for them the map zoom stays off.
  const ownsFocus = !!storeProps?.ownsFocus;
  // Filtering is a narrower permission than owning a focus: the toggle writes
  // the global well filter, so only the map focus flyout offers it. A chart's
  // own focus flyout owns a focus and still leaves it off.
  const allowsShapefileFilter = !!storeProps?.allowsShapefileFilter;
  // Monotonic id so a slow selection resolving after a newer one is discarded
  // instead of overwriting its columns and moving the map.
  const selectReqId = useRef(0);

  // Create ref for the tree select component
  const treeSelectRef = useRef<Maybe<any>>(null);

  // Report tree select ref to parent when it changes
  useEffect(() => {
    if (onSelectRef) {
      onSelectRef(TREE_SELECT_TOOLBAR_INDEX, treeSelectRef.current);
    }
  }, [onSelectRef, treeSelectRef.current]);

  // Facilities are a single point, so both match modes give the same answer,
  // and a GeoTIFF samples a raster rather than testing containment.
  const showFieldOptions =
    allowsShapefileFilter &&
    entityKind !== EntityKind.Facility &&
    !!selectedShapefile &&
    !isGeoTiffSelected;

  // The options block clears its own slots when it unmounts; this covers the
  // case where it never mounted at all, so its slots never point at buttons
  // from an earlier category.
  useEffect(() => {
    if (showFieldOptions || !onSelectRef) {
      return;
    }
    for (let offset = 0; offset < SHAPEFILE_FIELD_OPTIONS_TOOLBAR_COUNT; offset++) {
      onSelectRef(FIELD_OPTIONS_TOOLBAR_INDEX + offset, null);
    }
  }, [showFieldOptions, onSelectRef]);

  const selectedNode = selectedShapefile
    ? shapefilesDictRef.current[selectedShapefile]
    : undefined;
  const selectedShapefileName = selectedNode?.title || selectedNode?.name;

  const shapefilesQuery = useProjectShapefilesQuery({
    projectId
  });

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const fetchShapefiles = async () => {
      try {
        const [projectNodes, orgNodes]: [any, any] = await Promise.all([
          new Promise((resolve, reject) => {
            GetShapefileBranch(projectId, resolve, reject);
          }),
          new Promise((resolve, reject) => {
            GetOrgLevelShapefilesInProject(projectId, resolve, reject);
          })
        ]);

        const parseShapefileNodes = (node, shapefilesDict) => {
          const n = {
            value: node.shapefileId ? node.shapefileId : node.shapefileNodeId,
            title: node.title,
            selectable: node.type === "shapefile",
            children:
              node.children?.map((ch) => parseShapefileNodes(ch, shapefilesDict)) ?? []
          };
          if (node.type === "shapefile") {
            shapefilesDict[node.shapefileId] = node;
          }
          return n;
        };

        setSelectedShapefile(undefined);
        setIsGeoTiffSelected(false);
        // Clear out existing shapefiles
        shapefilesDictRef.current = {};

        const projectShapefileNodes = projectNodes.children
          .map((node) => parseShapefileNodes(node, shapefilesDictRef.current))
          // Remove empty folders
          .filter((node) => node.selectable || node.children.length > 0);

        const orgShapefileNodes = orgNodes.children
          .map((node) => parseShapefileNodes(node, shapefilesDictRef.current))
          // Remove empty folders
          .filter((node) => node.selectable || node.children.length > 0);

        const combinedShapefileNodes = [...projectShapefileNodes, ...orgShapefileNodes];

        // Filter nodes to only include those added to the currently selected project tree
        // const filteredShapefileNodes = combinedShapefileNodes.filter((node) =>
        //           selectedProject.tree.some((projectNode) => projectNode.key === node.value)
        // );

        setShapefiles(combinedShapefileNodes);
      } catch (error) {
        if (error.response?.data) {
          setErrorMessage(error.response?.data);
        } else {
          setErrorMessage("An error occurred while fetching shapefiles or GeoTIFFs.");
        }
      }
    };

    fetchShapefiles();
  }, [projectId, shapefilesQuery?.data]);

  const filteredShapefile = useMemo(
    () => filteringShapefileId(propertiesFilter, projectId),
    [propertiesFilter, projectId]
  );

  /**
   * The remembered pick, when it belongs to the project being read now. A
   * shapefile picked in another project is not in this tree, so it is nothing
   * to hand back.
   */
  const rememberedForProject =
    rememberedShapefile?.projectId === projectId ? rememberedShapefile : null;

  useEffect(() => {
    // Reading another project starts over. The pick is dropped rather than
    // left to sit until the user comes back, so nothing survives a project
    // change that the project change did not put there.
    if (rememberedShapefile && rememberedShapefile.projectId !== projectId) {
      rememberShapefile(null);
    }
  }, [projectId, rememberedShapefile, rememberShapefile]);

  useEffect(() => {
    // This category is thrown away every time the user reads another one and
    // built again on the way back, so the shapefile they were looking at is put
    // back from the two places it can be read: what they picked, which the
    // picker is holding, and failing that the shapefile the filter names, which
    // covers a picker opened fresh onto a filter that is already running.
    // Without either the dropdown reads empty and the filtering band is gone
    // while the filter behind them is still on.
    //
    // Only an empty selection is filled in — a shapefile picked since is
    // theirs — and only once the tree it belongs to has loaded, which is what
    // holds the name the filtering band writes with. Nothing here fetches or
    // moves the map: the geometry and the columns of what is being put back
    // were fetched when it was picked, and the map went where it was going
    // then.
    if (selectedShapefile) {
      return;
    }
    const restoredId = [rememberedForProject?.shapefileId, filteredShapefile].find(
      (id) => id && id in shapefilesDictRef.current
    );
    if (!restoredId) {
      return;
    }
    setSelectedShapefile(restoredId);
    setIsGeoTiffSelected(
      restoredId === rememberedForProject?.shapefileId
        ? rememberedForProject.isGeoTiff
        : false
    );
  }, [rememberedForProject, filteredShapefile, selectedShapefile, shapefiles]);

  function fetchShapefileGeoms(shapefileId: string): any {
    return new Promise((resolve, reject) => {
      GetShapefileFeatures(shapefileId, resolve, reject);
    });
  }

  // Fits the map to the shapefile that was just picked. The geoms are already
  // in hand here, so this costs no extra request. GeoTIFFs never reach this —
  // they short-circuit before the geometry fetch.
  function zoomToShapefile(geoms: Maybe<{ Geometry?: GeoJSON.Geometry }[]>) {
    const features = (geoms ?? [])
      .map((geom) => geom?.Geometry)
      .filter(Boolean)
      .map((geometry) => ({ type: "Feature" as const, geometry, properties: {} }));
    if (features.length === 0) {
      return;
    }
    const [minx, miny, maxx, maxy] = bbox({
      type: "FeatureCollection",
      features
    } as GeoJSON.FeatureCollection);
    // Empty or degenerate geometry gives infinite bounds; fitBounds would throw.
    if (![minx, miny, maxx, maxy].every(Number.isFinite)) {
      return;
    }
    setFlyToExtent({ minx, miny, maxx, maxy });
  }

  async function onSelectShapefile(shapefileId: string) {
    if (!(shapefileId in shapefilesDictRef.current)) {
      return;
    }
    const reqId = ++selectReqId.current;
    setSelectedShapefile(shapefileId);
    const shapefile = shapefilesDictRef.current[shapefileId];

    // Handle GeoTiff files differently - they have raster values instead of properties
    const isGeoTiff = shapefile.fileType === ShapefileFileType.GeoTiff;
    setIsGeoTiffSelected(isGeoTiff);
    // Remembered as it is picked, not when something is filtered with it: what
    // the user chose is theirs from the click, whether or not they go on to
    // switch the filtering on.
    rememberShapefile({ projectId, shapefileId, isGeoTiff });

    let columns: IGroupByListColumn[] = [];

    if (isGeoTiff) {
      // For GeoTiff, create a synthetic column for the raster value
      const rasterColumnName = "Raster Value";
      columns = [
        {
          title: rasterColumnName,
          id: rasterColumnName,
          canBin: true, // Raster values are numeric and can be binned
          dataType: "Number",
          canNormalize: false,
          group: ShapefileCategoryName,
          defaultUnit: "",
          normalizeByDefault: 1,
          subgroup: shapefile.title || shapefile.name,
          property: buildShapefileProperty(projectId, shapefileId, rasterColumnName)
        } as IGroupByListColumn
      ];
    } else {
      // For vector shapefiles, use the existing properties-based logic
      // A shapefile whose geometry will not load still filters and still lists
      // whatever columns can be read without it. The failure is said out loud,
      // though: the column list is read from the geometry, so silence here
      // reads as a shapefile with no fields in it.
      let geomGroup;
      let geomError;
      try {
        geomGroup = await fetchShapefileGeoms(shapefileId);
      } catch (error) {
        geomGroup = null;
        geomError = error;
      }
      if (reqId !== selectReqId.current) {
        return;
      }
      if (geomError) {
        setErrorMessage(
          geomError.response?.data ||
            "An error occurred while fetching the shapefile's features."
        );
      }
      if (ownsFocus) {
        zoomToShapefile(geomGroup?.geoms);
      }
      columns = shapefile.properties
        .map((column) => {
          // Iterate over the geoms in the shapefile to find the first non-null value for the column
          // stop iterating once a non-null value is found
          const firstNonNullGeom = findFirstNonNullValue(geomGroup?.geoms ?? [], column);
          if (firstNonNullGeom === null) {
            return null; // Skip column if it only contains nulls
          }
          const dataType = getDataType(firstNonNullGeom);
          return {
            title: column,
            id: column,
            canBin: dataType === "Number",
            dataType: dataType,
            canNormalize: false,
            group: ShapefileCategoryName,
            defaultUnit: "",
            normalizeByDefault: 1,
            subgroup: shapefile.title,
            property: buildShapefileProperty(projectId, shapefileId, column)
          } as IGroupByListColumn;
        })
        .filter(Boolean);
    }

    function getDataType(attribute): string {
      if (typeof attribute === "number") {
        return "Number";
      } else if (typeof attribute === "boolean") {
        return "Text";
      } else if (typeof attribute === "string") {
        // Further checks for date patterns, etc.
        if (!isNaN(Date.parse(attribute))) {
          return "Date";
        }
        return "Text";
      }
      return "Unknown";
    }

    function findFirstNonNullValue(geoms, column) {
      for (const geom of geoms) {
        const value = geom?.Properties?.[column];
        if (value !== null) {
          return value;
        }
      }
      return null;
    }

    // Rebased on the list in the store at dispatch time rather than the copy
    // this selection started from: a column merge or a second selection that
    // landed while the geometry was in flight would otherwise be overwritten.
    dispatch(setCategoryColumns(entityKind, ShapefileCategoryName, columns));
  }

  if (!isShapefileCategory) {
    return null;
  }

  return (
    <Wrapper>
      <TreeSelect
        ref={(el) => {
          treeSelectRef.current = el;
        }}
        className={classnames("toolbar-item", {
          "keyboard-focused":
            toolbarActive && toolbarFocusedIndex === TREE_SELECT_TOOLBAR_INDEX
        })}
        treeLine
        onSelect={onSelectShapefile}
        value={selectedShapefile}
        popupClassName="shapefile-tree-select"
        dropdownStyle={{ maxHeight: 400, overflow: "auto", zIndex: 2001 }}
        placeholder="Please select a Shapefile or GeoTIFF"
        treeData={shapefiles}
      />
      {showFieldOptions && (
        <ShapefileFieldOptions
          key={selectedShapefile}
          baseToolbarIndex={FIELD_OPTIONS_TOOLBAR_INDEX}
          projectId={projectId}
          shapefileId={selectedShapefile}
          shapefileName={selectedShapefileName}
          toolbarActive={toolbarActive}
          toolbarFocusedIndex={toolbarFocusedIndex}
          onSelectRef={onSelectRef}
        />
      )}
      {errorMessage && <Alert type="error" message={errorMessage} />}
    </Wrapper>
  );
}

export default ShapefileCategory;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* The whole shapefile column - this panel and the field list below - sits
     4px in from the list's stock 8px edge; the list side is FieldListInset in
     FilterableFieldList. Move both together or the red-line alignment dies. */
  padding: 0 var(--space-3);
  min-width: 100%;
  .ant-tree-select {
    width: 100%;
  }
`;
